/**
 * useScanBridge (Jul-2026, User-Wunsch) — 1-Klick-Direktscan über die lokale
 * TwoWheels Scan-Bridge (Windows, Port 18488). Gleiches Verfahren wie in der
 * Werkstatt-Scan-Erfassung: Scanner wird direkt angesteuert, Datei landet
 * ohne Umwege in der App. Merkt sich den Standard-Scanner (localStorage).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BRIDGE, maybeUpdateBridge } from "./scanBridgeUpdate";

export default function useScanBridge(onFile) {
    const [scanning, setScanning] = useState(false);
    const [repairing, setRepairing] = useState(false);
    const [devices, setDevices] = useState(null); // Array = Geräte-Auswahl offen
    const [bridgeMissing, setBridgeMissing] = useState(false);
    const [savedName, setSavedName] = useState(() => localStorage.getItem("tw_scan_device_name") || "");
    // Jul-2026 (User-Report FRAL) — Bridge-Status beim Öffnen prüfen (grüner Chip),
    // wie in der Werkstatt-Scan-Erfassung: Scanner wird sichtbar erkannt.
    const [online, setOnline] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const p = await fetch(`${BRIDGE}/ping`, { signal: AbortSignal.timeout(3000), targetAddressSpace: "loopback", cache: "no-store" });
                if (alive) setOnline(p.ok);
            } catch { if (alive) setOnline(false); }
        })();
        return () => { alive = false; };
    }, []);

    const saveDevice = (d) => {
        localStorage.setItem("tw_scan_device", d.id);
        localStorage.setItem("tw_scan_device_name", d.name || d.id);
        setSavedName(d.name || d.id);
    };

    const scanWith = async (deviceId) => {
        setDevices(null);
        setScanning(true);
        try {
            // Jul-2026 (User-Report FRAL) — exakt derselbe Scan-Aufruf wie in der
            // bewährten Werkstatt-Scan-Erfassung (CustomerPickerForOrder): keine
            // zusätzlichen Fetch-Optionen, Duplex ist Standard (wie dort).
            const params = new URLSearchParams();
            if (deviceId) params.set("device", deviceId);
            // Aug-2026 (User-Wunsch): Duplex ist standardmäßig AUS und wird nach
            // jedem Scan automatisch wieder ausgeschaltet (Einmal-Schalter).
            params.set("duplex", localStorage.getItem("tw_scan_duplex") === "1" ? "1" : "0");
            const qs = params.toString();
            const url = `${BRIDGE}/scan${qs ? `?${qs}` : ""}`;
            const r = await fetch(url, { signal: AbortSignal.timeout(420000) });
            setScanning(false);
            localStorage.setItem("tw_scan_duplex", "0");
            window.dispatchEvent(new CustomEvent("tw-duplex-reset"));
            if (!r.ok) { toast.error("Scanner-Fehler: " + (await r.text()).slice(0, 160)); return; }
            const blob = await r.blob();
            const isPdf = (blob.type || "").includes("pdf");
            onFile(new File([blob], `scan-${Date.now()}.${isPdf ? "pdf" : "jpg"}`, { type: blob.type || "image/jpeg" }));
        } catch {
            setScanning(false);
            toast.error("Scan abgebrochen oder Scan-Bridge nicht erreichbar.");
        }
    };

    // Jul-2026 (User-Wunsch): Reparatur-Button — behebt „Das WIA-Gerät ist
    // ausgelastet": gibt hängende Verbindungen frei und startet den
    // Windows-Bilderfassungsdienst (WIA) über die lokale Bridge neu.
    const repair = async () => {
        setRepairing(true);
        try {
            const r = await fetch(`${BRIDGE}/repair`, { signal: AbortSignal.timeout(60000), targetAddressSpace: "loopback", cache: "no-store" });
            if (r.status === 404) {
                toast.info('Ihre Scan-Bridge ist zu alt für die Reparatur-Funktion — sie aktualisiert sich beim nächsten „Mit PC scannen" automatisch (v7).');
            } else if (r.ok) {
                const d = await r.json().catch(() => ({}));
                if (d.ok) {
                    toast.success("Scanner-Reparatur ausgeführt — bitte erneut scannen.", {
                        description: (d.steps || []).join(" · ") || undefined, duration: 9000,
                    });
                    setOnline(true);
                } else {
                    toast.warning("Reparatur ausgeführt, aber der Scanner meldet sich noch nicht — Gerät bitte kurz aus- und wieder einschalten.", {
                        description: (d.steps || []).join(" · ") || undefined, duration: 10000,
                    });
                }
            } else {
                toast.error("Reparatur fehlgeschlagen: " + (await r.text()).slice(0, 160));
            }
        } catch {
            setOnline(false);
            toast.error("Scan-Bridge nicht erreichbar — bitte die Bridge am PC starten (Doppelklick auf TwoWheels-ScanBridge).");
        }
        setRepairing(false);
    };

    const start = async (forcePicker = false) => {
        setBridgeMissing(false);
        try {
            const ping = await fetch(`${BRIDGE}/ping`, { signal: AbortSignal.timeout(3500), targetAddressSpace: "loopback", cache: "no-store" });
            if (!ping.ok) throw new Error("bridge");
            setOnline(true);
            let list = [];
            let ver = 0;
            try {
                const info = await ping.json();
                ver = info.version || 0;
                maybeUpdateBridge(info); // Update-Hinweis / Selbst-Update (nicht blockierend)
                if (ver >= 3) {
                    const dr = await fetch(`${BRIDGE}/devices`, { signal: AbortSignal.timeout(8000), targetAddressSpace: "loopback", cache: "no-store" });
                    if (dr.ok) list = await dr.json();
                }
            } catch { /* ältere Bridge ohne Geräteliste */ }
            if (!Array.isArray(list) || list.length === 0) {
                if (forcePicker) {
                    toast.info(ver >= 3
                        ? "Die Bridge meldet keinen Scanner — Scanner einschalten/USB prüfen und erneut versuchen."
                        : `Bridge v${ver || "?"} unterstützt keine Geräteauswahl — bitte die neue Scan-Bridge herunterladen (Update-Hinweis folgt).`);
                }
                await scanWith("");
                return;
            }
            const saved = localStorage.getItem("tw_scan_device") || "";
            const savedDev = list.find((d) => d.id === saved);
            // Jul-2026 (User-Report FRAL): Auswahl auch bei nur EINEM Gerät anzeigen,
            // wenn explizit „Scanner wählen" geklickt wurde.
            if (forcePicker || (!savedDev && list.length > 1)) { setDevices(list); return; }
            const dev = savedDev || list[0];
            saveDevice(dev);
            await scanWith(dev.id);
        } catch {
            setOnline(false);
            setBridgeMissing(true);
        }
    };

    return { start, scanning, devices, setDevices, scanWith, saveDevice, savedName, bridgeMissing, setBridgeMissing, online, repair, repairing };
}
